<template>
  <v-card outlined class="pa-0">
    <v-card-text>
      <v-container fill-height fluid>
        <v-row justify="center">
          <v-col cols="12" lg="8" md="8">
            <h2 class="mb-4" style="text-align:center">Employee Military Information</h2>
          </v-col>
        </v-row>

        <v-row class="align-end">
          <v-col cols="12" sm="6" md="4" lg="3">
            <h4 class="field-label">Military Category / Категорія обліку</h4>
            <v-select
              v-model="employeeMilitaryInfo.Military_Category_ID"
              :items="militaryCategories"
              item-text="Category_Name"
              item-value="Military_Category_ID"
              hide-details
              dense
              :disabled="isLoading || !editMode"
              :rules="[rules.requiredText]"
            ></v-select>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg="3">
            <h4 class="field-label">Deferment Type / Тип відстрочки</h4>
            <v-select
              v-model="employeeMilitaryInfo.Type_of_Postponement_ID"
              :items="defermentTypes"
              item-text="Postponement_Name"
              item-value="Type_of_Postponement_ID"
              hide-details
              dense
              :disabled="!editMode"
            ></v-select>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg="3">
            <h4 class="field-label">Deferment End Date (MRD) / Кінцева дата відстрочки по ВОД</h4>
            <DatePicker
              v-if="editMode"
              v-model="employeeMilitaryInfo.Postponement_End_Date_By_VOD"
              hide-details
              clearable
            />
            <v-text-field
              v-else
              :value="employeeMilitaryInfo.Postponement_End_Date_By_VOD"
              disabled
              readonly
              dense
              hide-details
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg="3">
            <h4 class="field-label">Deferment Eligibility End / Кінцева дата права на відстрочку</h4>
            <DatePicker
              v-if="editMode"
              v-model="employeeMilitaryInfo.Postponement_Right_End_Date"
              hide-details
              clearable
            />
            <v-text-field
              v-else
              :value="employeeMilitaryInfo.Postponement_Right_End_Date"
              disabled
              readonly
              dense
              hide-details
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg="3">
            <h4 class="field-label">Military Registration Violation / Порушення ВО</h4>
            <v-text-field
              v-model="employeeMilitaryInfo.Military_Registration_Violations"
              :disabled="!editMode"
              dense
              hide-details
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg="3">
            <h4 class="field-label">Employer (LLC) / Працевлаштований на ТОВ</h4>
            <v-text-field
              v-model="employeeMilitaryInfo.Employed_At_LLC"
              :disabled="!editMode"
              dense
              hide-details
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg="3">
            <h4 class="field-label">MRD File (Reserve+) / Файл ВОД з Резерв+</h4>

            <v-text-field
              v-if="editMode"
              v-model="employeeMilitaryInfo.Document_Name"
              dense
              hide-details
              :disabled="isLoading"
            >
              <template v-slot:append>
                <v-file-input
                  hide-input
                  hide-details
                  dense
                  @change="onFileChange"
                  v-model="employeeMilitaryInfo.File"
                  accept="application/pdf, image/*"
                ></v-file-input>
              </template>
            </v-text-field>

            <div
              v-else
              :class="['custom-file-viewer', { 'no-file': !employeeMilitaryInfo.Document_Name }]"
              @click="employeeMilitaryInfo.Document_Name ? downloadDocument(employeeMilitaryInfo) : null"
            >
              <span class="file-name text-truncate">
                {{ employeeMilitaryInfo.Document_Name || 'No file uploaded' }}
              </span>
            </div>
          </v-col>
        </v-row>

        <v-row class="mt-6">
          <v-spacer></v-spacer>
          <v-btn
            v-if="currentUser || isFinancier || canEditMilitaryInfo"
            color="success"
            small
            class="mr-3"
            :disabled="editMode && !canSave"
            @click.prevent="editMilitaryInformation"
          >
            {{ editMode ? 'Save' : 'Edit' }}
          </v-btn>
          <v-btn
            v-if="editMode"
            color="red darken-2 white--text"
            small
            @click.prevent="cancelChanges"
          >
            Cancel
          </v-btn>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'
export default {
  name: 'EmployeeMilitaryInfo',
  components: {
    DatePicker: () => import('@/components/global/DatePicker.vue')
  },
  props: {
    employee: { required: false },
    user: { required: false },
    rolesOfUser: { required: true },
    canEditMilitaryInfo: { type: Boolean, default: false }
  },
  data () {
    return {
      defermentTypes: [],
      employeeMilitaryInfo: {
        Military_ID: null,
        Employee_ID: this.employee.Employee_ID,
        Military_Category_ID: null,
        Type_of_Postponement_ID: null,
        Military_Status_ID: null,
        File: null,
        Extension: '',
        Document_Name: '',
        S3_Name: '',
        Location: '',
        Date_Updated_On_Time: false,
        Grounds_For_Postponement: false,
        Grounds_For_Postponement_Description: '',
        Postponement_to_Date: null,
        Date_of_Update: formatLocalDate(),
        Postponement_End_Date_By_VOD: null,
        Postponement_Right_End_Date: null,
        Military_Registration_Violations: null,
        Employed_At_LLC: null
      },
      militaryCategories: [],
      copyOfMilitaryInfo: null,
      downloadLink: document.createElement('a'),
      editMode: false,
      isLoading: false,
      rules: {
        requiredText: v => !!v || 'Required'
      }
    }
  },
  mounted () {
    this.getEmployeeMilitaryInfo()
    this.getEmployeeMilitaryCategories()
    this.getEmployeeDefermentTypes()
  },
  computed: {
    canSave () {
      return !!this.employeeMilitaryInfo.Military_Category_ID
    },
    currentUser () {
      return this.employee ? this.user.employee_id === this.employee.Employee_ID : false
    },
    isFinancier () {
      const requiredRole = 'financier'
      return this.rolesOfUser.some(role => role.RoleName.toLowerCase() === requiredRole)
    }
  },
  methods: {
    parseFileNameAndExtension (fullFileName) {
      const name = fullFileName.slice(0, fullFileName.lastIndexOf('.')).trim()
      const extension = fullFileName.split('.').pop()
      return { name, extension }
    },
    getEmployeeMilitaryInfo () {
      this.isLoading = true
      this.axios.get(`/api/employee_military/${this.employee.Employee_ID}`, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(res => {
        if (res.data && res.data.length) {
          this.employeeMilitaryInfo = { ...this.employeeMilitaryInfo, ...res.data[0] }
        }
        this.isLoading = false
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getEmployeeMilitaryCategories () {
      this.axios.get('/api/military_categories', {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(res => {
        this.militaryCategories = res.data
        if (res.data?.length && !this.employeeMilitaryInfo.Military_Category_ID) {
          this.employeeMilitaryInfo.Military_Category_ID = res.data[0].Military_Category_ID
        }
      })
    },
    getEmployeeDefermentTypes () {
      this.axios.get('/api/deferment_types', {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(res => {
        this.defermentTypes = res.data
      })
    },
    postEmployeeMilitaryInfo () {
      this.isLoading = true

      const formData = new FormData()
      const payload = {
        ...this.employeeMilitaryInfo,
        Date_of_Update: formatLocalDate(),
        S3_Name: this.employeeMilitaryInfo.S3_Name || '',
        Grounds_For_Postponement_Description: this.employeeMilitaryInfo.Grounds_For_Postponement
          ? this.employeeMilitaryInfo.Grounds_For_Postponement_Description
          : null
      }

      Object.entries(payload).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value)
        }
      })

      this.axios.post('/api/employee_military', formData, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      })
        .then(() => {
          this.$store.dispatch('setSuccess', { successMsg: 'Military info updated' })
          this.finalizeRequest()
        })
        .catch(error => {
          const errorMsg = error.response?.data || error.message
          if (errorMsg.includes('successful') || errorMsg.includes('Cloud')) {
            this.$store.dispatch('setSuccess', { successMsg: 'Military info updated' })
            this.finalizeRequest()
          } else {
            this.$store.dispatch('setError', { errorMsg: 'Server Error: ' + errorMsg })
            this.isLoading = false
          }
        })
    },
    finalizeRequest () {
      this.getEmployeeMilitaryInfo()
      this.editMode = false
      this.isLoading = false
    },
    onFileChange (e) {
      if (e) {
        const { name, extension } = this.parseFileNameAndExtension(e.name)
        this.employeeMilitaryInfo.Document_Name = name
        this.employeeMilitaryInfo.Extension = extension
      }
    },
    downloadDocument (item) {
      if (!item.S3_Name) return
      this.isLoading = true
      this.axios.get(`/api/document/${item.S3_Name}`, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(res => {
        const { ContentType, Body } = res.data
        this.openFile(item.Document_Name, Body.data, ContentType)
        this.isLoading = false
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
        this.isLoading = false
      })
    },
    openFile (filename, data, type) {
      const bytes = new Uint8Array(data)
      let binary = ''
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i])
      }
      this.downloadLink.href = `data:${type};base64,${window.btoa(binary)}`
      this.downloadLink.download = filename
      this.downloadLink.click()
    },
    editMilitaryInformation () {
      if (this.editMode) {
        this.postEmployeeMilitaryInfo()
      } else {
        this.copyOfMilitaryInfo = { ...this.employeeMilitaryInfo }
        this.editMode = true
      }
    },
    cancelChanges () {
      this.employeeMilitaryInfo = { ...this.copyOfMilitaryInfo }
      this.editMode = false
    }
  }
}
</script>

<style scoped>
.field-label {
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 8px;
  min-height: 40px;
  display: flex;
  align-items: flex-end;
  color: var(--v-theme-on-surface-variant);
}

.custom-file-viewer {
  display: flex;
  align-items: center;
  height: 32px;
  border-bottom: 1px dotted var(--v-theme-on-surface-variant);
  cursor: pointer;
  transition: 0.2s;
}

.custom-file-viewer.no-file {
  cursor: default;
  border-bottom-style: solid;
  opacity: 0.6;
}

.file-name {
  font-size: 1rem;
  max-width: 100%;
}

.theme--light.v-card .v-input--is-disabled {
  opacity: 1 !important;
}

.theme--light.v-card ::v-deep .v-input--is-disabled input {
  color: #000000 !important;
  -webkit-text-fill-color: #000000 !important;
}

.theme--light.v-card ::v-deep .v-input--is-disabled .v-select__selection {
  color: #000000 !important;
  opacity: 1 !important;
}

.theme--light.v-card .custom-file-viewer .file-name {
  color: #000000 !important;
}

.theme--light.v-card ::v-deep .v-input--is-disabled .v-input__slot::before {
  border-image: none !important;
  border-style: solid !important;
  border-color: rgba(0, 0, 0, 0.42) !important;
}

.theme--light.v-card ::v-deep .v-input--is-disabled .v-input__append-inner {
  display: none !important;
}

.theme--dark .file-name {
  color: var(--v-theme-on-surface-variant);
}

.custom-file-viewer:not(.no-file):hover {
  border-bottom-color: var(--v-theme-on-surface);
}

.custom-file-viewer:not(.no-file):hover .file-name {
  color: var(--v-theme-on-surface) !important;
}
</style>
